import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useBuilderStore } from '../../../stores/builderStore';
import { useThemeStore } from '../../../stores/themeStore';
import MobileFieldPalette from './MobileFieldPalette';
import MobileFormCanvas from './MobileFormCanvas';
import MobileFieldConfigurator from './MobileFieldConfigurator';
import MobileFormStyling from './MobileFormStyling';
import MobileLanguageSelector from '../MobileLanguageSelector';
import FormPreview from '../../builder/FormPreview';
import MobileErrorBoundary from '../MobileErrorBoundary';

interface MobileBuilderProps {
  formId?: string;
  formName?: string;
  onSave?: () => Promise<void>;
  onBack?: () => void;
}

type ActiveSheet = 'none' | 'palette' | 'configurator' | 'menu';

export default function MobileBuilder({ formId: _formId, formName, onSave, onBack }: MobileBuilderProps) {
  // formId reserved for future use (e.g., auto-save, analytics)
  void _formId;

  const { t } = useTranslation('mobile');
  const { formSchema, hasUnsavedChanges, selectedFieldId, selectField } = useBuilderStore();
  useThemeStore(); // Keep store subscription for dark mode reactivity

  // Get undo/redo functions fresh each time to avoid stale references
  const handleUndo = useCallback(() => {
    useBuilderStore.temporal.getState().undo();
  }, []);

  const handleRedo = useCallback(() => {
    useBuilderStore.temporal.getState().redo();
  }, []);

  const [activeSheet, setActiveSheet] = useState<ActiveSheet>('none');
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isStylingOpen, setIsStylingOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [showSaveError, setShowSaveError] = useState(false);
  const [showDesktopHint, setShowDesktopHint] = useState(false);

  // Refs for save handling
  const isSavingRef = useRef(false);
  const saveSuccessTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveErrorTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Track undo/redo availability
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  useEffect(() => {
    const unsubscribe = useBuilderStore.temporal.subscribe((state) => {
      setCanUndo(state.pastStates.length > 0);
      setCanRedo(state.futureStates.length > 0);
    });
    return unsubscribe;
  }, []);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (saveSuccessTimeoutRef.current) {
        clearTimeout(saveSuccessTimeoutRef.current);
      }
      if (saveErrorTimeoutRef.current) {
        clearTimeout(saveErrorTimeoutRef.current);
      }
    };
  }, []);

  const handleAddField = useCallback(() => {
    setActiveSheet('palette');
  }, []);

  const handleFieldSelect = useCallback((fieldId: string) => {
    selectField(fieldId);
    setActiveSheet('configurator');
  }, [selectField]);

  const handleCloseSheet = useCallback(() => {
    setActiveSheet('none');
  }, []);

  const handleSave = useCallback(async () => {
    if (!onSave || isSavingRef.current) return;
    isSavingRef.current = true;
    setIsSaving(true);
    try {
      await onSave();
      setShowSaveSuccess(true);
      saveSuccessTimeoutRef.current = setTimeout(() => setShowSaveSuccess(false), 2000);
    } catch (error) {
      console.error('Failed to save:', error);
      setShowSaveError(true);
      saveErrorTimeoutRef.current = setTimeout(() => setShowSaveError(false), 3000);
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  }, [onSave]);

  const isQuizForm = formSchema.settings?.quiz?.enabled === true;

  return (
    <div className="h-screen flex flex-col bg-gray-100 dark:bg-gray-950">
      {/* Header */}
      <header className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-4 py-3 safe-area-inset-top">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {onBack && (
              <button
                onClick={onBack}
                aria-label={t('common.back')}
                className="p-2 -ml-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg touch-manipulation"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            <div className="min-w-0">
              <h1 className="font-semibold text-gray-900 dark:text-white truncate">
                {formName || t('builder.title')}
              </h1>
              <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                <span>{t('builder.fieldCount', { count: formSchema.fields.length })}</span>
                {hasUnsavedChanges && (
                  <>
                    <span className="w-1 h-1 bg-orange-500 rounded-full" />
                    <span className="text-orange-500">{t('builder.unsaved')}</span>
                  </>
                )}
                {isQuizForm && (
                  <>
                    <span>•</span>
                    <span className="text-purple-500">{t('builder.quiz')}</span>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Undo/Redo */}
            <button
              onClick={handleUndo}
              disabled={!canUndo}
              aria-label="Undo"
              className="p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg disabled:opacity-40 touch-manipulation"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
              </svg>
            </button>
            <button
              onClick={handleRedo}
              disabled={!canRedo}
              aria-label="Redo"
              className="p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg disabled:opacity-40 touch-manipulation"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10h-10a8 8 0 00-8 8v2M21 10l-6 6m6-6l-6-6" />
              </svg>
            </button>

            {/* Menu */}
            <button
              onClick={() => setActiveSheet('menu')}
              aria-label="Open menu"
              className="p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg touch-manipulation"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
              </svg>
            </button>
          </div>
        </div>
      </header>

      {/* Canvas */}
      <MobileErrorBoundary>
        <MobileFormCanvas
          onFieldSelect={handleFieldSelect}
          onAddField={handleAddField}
        />
      </MobileErrorBoundary>

      {/* Bottom Action Bar */}
      <div className="bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 px-4 py-3 safe-area-inset-bottom">
        <div className="flex items-center justify-between gap-3">
          <button
            onClick={handleAddField}
            className="flex-1 flex items-center justify-center gap-2 py-3 bg-primary-600 text-white rounded-xl font-medium shadow-lg shadow-primary-600/30 active:scale-[0.98] transition-transform touch-manipulation"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {t('builder.addField')}
          </button>

          <button
            onClick={() => setIsPreviewOpen(true)}
            className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 rounded-xl font-medium active:scale-[0.98] transition-transform touch-manipulation"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            {t('common.preview')}
          </button>

          {onSave && (
            <button
              onClick={handleSave}
              disabled={isSaving || !hasUnsavedChanges}
              className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-medium active:scale-[0.98] transition-all touch-manipulation ${
                hasUnsavedChanges
                  ? 'bg-green-600 text-white shadow-lg shadow-green-600/30'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-400'
              }`}
            >
              {isSaving ? (
                <svg className="w-5 h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              ) : showSaveSuccess ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                </svg>
              )}
              {t('common.save')}
            </button>
          )}
        </div>
      </div>

      {/* Field Palette Sheet */}
      {activeSheet === 'palette' && (
        <div className="fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black/50 animate-fadeIn"
            onClick={handleCloseSheet}
          />
          <div className="absolute bottom-0 left-0 right-0 h-[85%] bg-white dark:bg-gray-900 rounded-t-3xl shadow-2xl animate-slideUp">
            <MobileFieldPalette onClose={handleCloseSheet} />
          </div>
        </div>
      )}

      {/* Field Configurator Sheet */}
      <MobileFieldConfigurator
        isOpen={activeSheet === 'configurator' && !!selectedFieldId}
        onClose={handleCloseSheet}
      />

      {/* Menu Sheet */}
      {activeSheet === 'menu' && (
        <div className="fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black/50 animate-fadeIn"
            onClick={handleCloseSheet}
          />
          <div className="absolute bottom-0 left-0 right-0 bg-white dark:bg-gray-800 rounded-t-3xl shadow-2xl animate-slideUp safe-area-inset-bottom">
            <div className="flex justify-center py-3">
              <div className="w-12 h-1.5 bg-gray-300 dark:bg-gray-600 rounded-full" />
            </div>
            <div className="px-4 pb-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('builder.formOptions')}</h2>
              <div className="space-y-2">
                <button
                  onClick={() => {
                    handleCloseSheet();
                    setIsPreviewOpen(true);
                  }}
                  className="w-full flex items-center gap-4 p-4 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-xl transition-colors touch-manipulation"
                >
                  <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
                    <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-gray-900 dark:text-white">{t('builder.previewForm')}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{t('builder.seeHowFormLooks')}</p>
                  </div>
                </button>

                <button
                  onClick={() => {
                    handleCloseSheet();
                    setIsStylingOpen(true);
                  }}
                  className="w-full flex items-center gap-4 p-4 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-xl transition-colors touch-manipulation"
                >
                  <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center">
                    <svg className="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                    </svg>
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-gray-900 dark:text-white">{t('builder.formStyling')}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{t('builder.customizeColors')}</p>
                  </div>
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>

                <button
                  onClick={() => {
                    handleCloseSheet();
                    setShowDesktopHint(true);
                    setTimeout(() => setShowDesktopHint(false), 3000);
                  }}
                  className="w-full flex items-center gap-4 p-4 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-xl transition-colors touch-manipulation"
                >
                  <div className="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-xl flex items-center justify-center">
                    <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-gray-900 dark:text-white">{t('builder.formSettings')}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{t('builder.submitMessages')}</p>
                  </div>
                  <span className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-gray-500 dark:text-gray-400">{t('builder.desktopOnly')}</span>
                </button>

                {/* Language Selector */}
                <MobileLanguageSelector onClose={handleCloseSheet} />

                <div className="pt-4 border-t border-gray-200 dark:border-gray-700 mt-4">
                  <p className="text-xs text-gray-400 dark:text-gray-500 text-center mb-3">
                    {t('builder.desktopAdvanced')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      <FormPreview isOpen={isPreviewOpen} onClose={() => setIsPreviewOpen(false)} />

      {/* Form Styling Modal */}
      {_formId && (
        <MobileFormStyling
          formId={_formId}
          isOpen={isStylingOpen}
          onClose={() => setIsStylingOpen(false)}
        />
      )}

      {/* Save Success Toast */}
      {showSaveSuccess && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 animate-slideDown">
          <div className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-full shadow-lg">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="font-medium">{t('builder.saved')}</span>
          </div>
        </div>
      )}

      {/* Save Error Toast */}
      {showSaveError && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 animate-slideDown">
          <div className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-full shadow-lg">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span className="font-medium">{t('builder.saveFailed')}</span>
          </div>
        </div>
      )}

      {/* Desktop Feature Hint Toast */}
      {showDesktopHint && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 animate-slideDown">
          <div className="flex items-center gap-2 px-4 py-2 bg-gray-800 dark:bg-gray-700 text-white rounded-full shadow-lg">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <span className="font-medium">{t('builder.availableOnDesktop')}</span>
          </div>
        </div>
      )}
    </div>
  );
}
